import React, { useEffect } from "react";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import MuiLink from "@mui/material/Link";
import { ListItemIcon, TextField } from "@outpave/ds";
import { fieldErrorText } from "utils/formikFieldError";
import Typography from "@mui/material/Typography";
import { Trans, useTranslation } from "react-i18next";
import { FormikProps } from "formik";
import { BankIcon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import ContactType from "enums/ContactType";
import { FormikContact } from "../../../hooks/useContactData";
import Box from "@mui/material/Box";
import { Columns, Inline, Stack } from "components/layout/reusableLayouts";

interface AddPaymentDetailsToContactProps {
  contactFormik: FormikProps<FormikContact>;
  linkToBusinessAccount?: (businessContactId: string) => void;
  customDescription?: string;
}

const SECONDARY_SX = { color: "var(--color-text-secondary)" } as const;

const AddPaymentDetailsToContact: React.FC<AddPaymentDetailsToContactProps> = ({
  contactFormik,
  linkToBusinessAccount,
  customDescription = "RequiredForBillPay",
}) => {
  const { t } = useTranslation();

  const isIndividualWithBusiness =
    contactFormik.values.contactType === ContactType.Individual &&
    !!contactFormik.values.parentContactId;

  useEffect(() => {
    contactFormik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactFormik.values.showPaymentDetails]);

  if (
    contactFormik.values.paymentBankInfo &&
    !contactFormik.values.showPaymentDetails
  ) {
    return contactFormik.values.removeBank ? (
      <Inline
        justify="between"
        align="center"
        sx={{ marginBlockEnd: "var(--space-4)" }}
      >
        <Box sx={{ width: "66%" }}>
          <TextField
            fullWidth
            disabled
            label={t("Bank Account")}
            name="paymentBankInfo"
            value={contactFormik.values.paymentBankInfo}
            helperText={t("This account will be removed when you save")}
          />
        </Box>
        <Button
          variant="primary-outlined"
          size="small"
          onClick={() => {
            contactFormik.setFieldValue("removeBank", false);
          }}
        >
          {t("Undo")}
        </Button>
      </Inline>
    ) : (
      <Inline
        justify="between"
        align="center"
        sx={{ marginBlockEnd: "var(--space-4)" }}
      >
        <Box sx={{ width: "66%" }}>
          <TextField
            fullWidth
            readOnly
            label={t("Bank Account")}
            name="paymentBankInfo"
            value={contactFormik.values.paymentBankInfo}
          />
        </Box>
        <Inline gap="var(--space-2)">
          {!isIndividualWithBusiness && (
            <Button
              variant="primary-outlined"
              size="small"
              onClick={() => {
                contactFormik.setFieldValue("showPaymentDetails", true);
              }}
            >
              {t("Edit")}
            </Button>
          )}
          <Button
            variant="danger-outlined"
            size="small"
            onClick={() => {
              contactFormik.setFieldValue("paymentDetails", {
                accountName: "",
                accountNumber: "",
                routingNumber: "",
              });
              contactFormik.setFieldValue("removeBank", true);
            }}
          >
            {t("Remove")}
          </Button>
        </Inline>
      </Inline>
    );
  }

  if (isIndividualWithBusiness) {
    return (
      <Card variant="outlined" sx={{ padding: "var(--space-5)" }}>
        <Typography variant="h6">
          {t("Banking Details Managed By Business")}
        </Typography>
        <Typography variant="body2" sx={SECONDARY_SX}>
          <Trans
            i18nKey="ContactAttachedToBusinessPaymentDescription"
            components={{
              // No children: the words live inside the tag in the
              // translation, and passing them here overrode the Spanish with
              // an untranslated fallback. A clickable span is also the one
              // affordance here that a keyboard cannot reach, so this is a
              // real button wearing the sentence's type.
              business_link: (
                <MuiLink
                  component="button"
                  type="button"
                  onClick={() =>
                    contactFormik.values.parentContactId &&
                    linkToBusinessAccount?.(
                      contactFormik.values.parentContactId,
                    )
                  }
                  sx={{
                    font: "inherit",
                    verticalAlign: "baseline",
                    padding: 0,
                    color: "var(--color-text-brand)",
                  }}
                />
              ),
            }}
          />
        </Typography>
      </Card>
    );
  }

  const field = (
    name: "accountName" | "accountNumber" | "routingNumber",
    label: string,
  ) => (
    <TextField
      fullWidth
      label={label}
      name={`paymentDetails.${name}`}
      value={contactFormik.values.paymentDetails[name]}
      onChange={contactFormik.handleChange}
      onBlur={contactFormik.handleBlur}
      errorText={fieldErrorText(contactFormik, `paymentDetails.${name}`)}
    />
  );

  return (
    <Card variant="outlined" sx={{ padding: "var(--space-5)" }}>
      <Stack gap="var(--space-5)">
        {/* A call-to-action row: the glyph leads, the copy explains, the
            action sits at the end. The bank icon used to float alone on the
            right of a heading, which read as decoration rather than as the
            subject of the row. */}
        <Inline gap="var(--space-4)" align="center" wrap={false}>
          <ListItemIcon wrapper="brand">
            <HugeiconsIcon
              icon={BankIcon}
              size={20}
              color="var(--color-text-brand)"
            />
          </ListItemIcon>

          <Stack gap="var(--space-1)" sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2">
              {t("Add Banking Details")}
            </Typography>
            <Typography variant="body2" sx={SECONDARY_SX}>
              {t(customDescription)}
            </Typography>
          </Stack>

          {!contactFormik.values.showPaymentDetails && (
            <Button
              variant="primary-outlined"
              size="small"
              onClick={() => {
                contactFormik.setFieldValue("showPaymentDetails", true);
              }}
            >
              {t("Add Bank")}
            </Button>
          )}
        </Inline>

        {contactFormik.values.showPaymentDetails && (
          <Stack gap="var(--space-4)">
            {field("accountName", t("Account Name"))}
            <Columns count={2} gap="var(--space-4)">
              {field("accountNumber", t("Account Number"))}
              {field("routingNumber", t("Routing Number"))}
            </Columns>

            <Inline justify="end">
              <Button
                variant="default-outlined"
                size="small"
                onClick={() => {
                  contactFormik.setFieldValue("showPaymentDetails", false);
                  contactFormik.setFieldValue("paymentDetails", {
                    accountName: "",
                    accountNumber: "",
                    routingNumber: "",
                  });
                }}
              >
                {t("Cancel")}
              </Button>
            </Inline>
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

export default AddPaymentDetailsToContact;
